import classNames from 'classnames';
import React, { ReactEventHandler, useCallback, useEffect, useState } from 'react';
import {
  SeasonFragment,
  SeasonLeagueFragment,
  TeamListItemFragment,
} from '@amf/shared/types/graphql';
import { useTranslate } from '@tolgee/react';
import SelectField from '@amf/shared/components/form/SelectField';
import useTeamStats from '@amf/shared/data/hooks/useTeamStats';
import Loader from '@amf/shared/components/utils/Loader';
import Tippy from '@tippyjs/react';
import Link from 'next/link';
import Button from '@amf/shared/components/button/Button';
import { findTeamDefaults } from '@amf/shared/data/Team';

interface TeamStatsSegmentProps {
  value: number;
  valueFixedNumber?: number;
  accessoryValue?: number;
  accessoryTitle?: string;
  title: string;
}

function TeamStatsSegment({
  title,
  value,
  valueFixedNumber,
  accessoryValue,
  accessoryTitle,
}: TeamStatsSegmentProps) {
  return (
    <div className='TeamStats__segment'>
      <div className='TeamStats__segmentTitle'>{title}</div>
      <div className='TeamStats__segmentValue'>
        {valueFixedNumber === undefined ? value : value.toFixed(valueFixedNumber)}
        {accessoryValue !== undefined && accessoryTitle && (
          <Tippy content={accessoryTitle} animation='shift-away' theme='light'>
            <span>({accessoryValue.toFixed(1)})</span>
          </Tippy>
        )}
      </div>
    </div>
  );
}

interface TeamStatsProps {
  team: TeamListItemFragment;
  seasons: SeasonFragment[];
  prefixes: { table: string };
  title?: string;
  center?: boolean;
}

function TeamStats({ title, center, seasons, team, prefixes }: TeamStatsProps) {
  const { t } = useTranslate();
  const [season, setSeason] = useState<SeasonFragment | null>(null);
  const [league, setLeague] = useState<SeasonLeagueFragment | null>(null);
  const { stats, loading } = useTeamStats(team.id, league?.id, season?.id);

  useEffect(() => {
    if (seasons.length === 0) return;
    const { league, season } = findTeamDefaults(team, seasons);
    setSeason(season);
    setLeague(league);
  }, [seasons, team]);

  const changeSeason = useCallback<ReactEventHandler<HTMLSelectElement>>(
    e => {
      const season = seasons.find(s => s.id === e.currentTarget.value);
      if (!season) {
        setSeason(null);
        setLeague(null);
        return;
      }
      setSeason(season);
      setLeague(season.leagues[0]);
    },
    [seasons],
  );

  const changeLeague = useCallback<ReactEventHandler<HTMLSelectElement>>(
    e => {
      const league = season.leagues.find(l => l.id === e.currentTarget.value);
      if (!league) {
        setLeague(null);
        return;
      }
      setLeague(league);
    },
    [season],
  );

  return (
    <div className='TeamStats'>
      <div className='TeamStats__header'>
        {title && (
          <h2
            className={classNames('TeamStats__title', {
              'TeamStats__title--center': center,
            })}
          >
            {title}
          </h2>
        )}
        <div className='TeamStats__filter'>
          <SelectField
            label={t('team.stats.filter.season')}
            value={season?.id}
            onChange={changeSeason}
          >
            {seasons.map(season => (
              <option key={season.id} value={season.id}>
                {season.name}
              </option>
            ))}
            <option value={null}>{t('team.stats.filter.season.all')}</option>
          </SelectField>
          <SelectField
            label={t('team.stats.filter.league')}
            value={league?.id}
            onChange={changeLeague}
            disabled={season === null}
          >
            {season?.leagues.map(league => (
              <option key={league.id} value={league.id}>
                {league.name}
              </option>
            ))}
            <option value={null}>{t('team.stats.filter.league.all')}</option>
          </SelectField>
        </div>
      </div>
      {loading && <Loader size='small'>{t('loading')}</Loader>}
      {!loading && stats && (
        <>
          <div className='TeamStats__content'>
            <div className='TeamStats__columns'>
              <TeamStatsSegment value={stats.matches} title={t('team.stats.segment.matches')} />
              <TeamStatsSegment
                value={stats.goals}
                accessoryValue={stats.matches === 0 ? undefined : stats.goals / stats.matches}
                accessoryTitle={t('team.stats.segment.goals.avg')}
                title={t('team.stats.segment.goals')}
              />
              <TeamStatsSegment value={stats.wins} title={t('team.stats.segment.wins')} />
              <TeamStatsSegment
                value={stats.winsInExtension}
                title={t('team.stats.segment.winsInExtension')}
              />
              <TeamStatsSegment
                value={stats.winsWithoutGoal}
                title={t('team.stats.segment.winsWithoutGoal')}
              />
              <TeamStatsSegment value={stats.draws} title={t('team.stats.segment.draws')} />
            </div>
            <div className='TeamStats__columns'>
              <TeamStatsSegment value={stats.lost} title={t('team.stats.segment.lost')} />
              <TeamStatsSegment
                value={stats.receivedGoals}
                accessoryValue={
                  stats.matches === 0 ? undefined : stats.receivedGoals / stats.matches
                }
                accessoryTitle={t('team.stats.segment.receivedGoals.avg')}
                title={t('team.stats.segment.receivedGoals')}
              />
              <TeamStatsSegment
                value={stats.yellowCards}
                title={t('team.stats.segment.yellowCards')}
              />
              <TeamStatsSegment value={stats.redCards} title={t('team.stats.segment.redCards')} />
              <TeamStatsSegment value={stats.points} title={t('team.stats.segment.points')} />
              <TeamStatsSegment
                value={stats.age}
                title={t('team.stats.segment.age')}
                valueFixedNumber={1}
              />
            </div>
          </div>
          <div className='TeamStats__more'>
            <Link href={prefixes.table}>
              <a>
                <Button variant='dark-outline'>{t('team.stats.more')}</Button>
              </a>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

export default TeamStats;
